import React, { useState, useEffect, useRef, useCallback } from 'react';
import Draggable from 'react-draggable';
import CheckboxList from './NodesCheckList';
import ChartCrosshair from './chartjs-plugin-crosshair';
import { Grid, Icon, Button } from 'semantic-ui-react';
import AnalysisPicker from './AnalysisPicker';
import { getCurrentTheme, themes } from './themes/ThemeManager';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Colors // Add Colors plugin
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(
  ChartCrosshair,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Colors // Register Colors plugin
);

// Disable default colors plugin to prevent it from injecting non-theme colors (like blue)
ChartJS.defaults.plugins.colors = {
  enabled: false,
  forceOverride: false
};

// Beautiful chart colors - always use these regardless of theme
// These are the original warm palette for sweep/AC plots
const beautifulChartColors = [
  '#A95C44', // Rust
  '#2D6A6A', // Deep Teal
  '#C09541', // Golden Mustard
  '#4E5D6C', // Slate Blue
  '#6B705C', // Moss Green
  '#8B3D30', // Auburn
  '#436450', // Jade Earth
  '#5C4D66', // Muted Plum
  '#3D3530'  // Theme Brown
];

// Voltage chart - light blue background behind waves
const voltageChartColors = [
  '#66CCFF', // Light Blue (line)
  '#2D6A6A', // Deep Teal (line)
  '#A95C44', // Rust (line)
  '#C09541', // Golden Mustard (line)
  '#4E5D6C', // Slate Blue (line)
  '#6B705C', // Moss Green (line)
  '#8B3D30', // Auburn (line)
  '#436450', // Jade Earth (line)
  '#5C4D66'  // Muted Plum (line)
];
const voltageChartBg = '#E6F7FF'; // Light blue background behind waves

// Amperes chart - light yellow/amber background behind waves
const amperesChartColors = [
  '#FFEE66', // Light Yellow (line)
  '#C09541', // Golden Mustard (line)
  '#A95C44', // Rust (line)
  '#2D6A6A', // Deep Teal (line)
  '#4E5D6C', // Slate Blue (line)
  '#6B705C', // Moss Green (line)
  '#8B3D30', // Auburn (line)
  '#436450', // Jade Earth (line)
  '#5C4D66'  // Muted Plum (line)
];
const amperesChartBg = '#FFF9E6'; // Light yellow background behind waves

/**
 * Get chart colors from the current theme
 * @returns {Object} Chart-related colors from the current theme
 */
const getChartThemeColors = () => {
  const themeName = getCurrentTheme();
  const theme = themes[themeName];
  if (!theme) {
    // Fallback colors - always use beautiful chart palette
    return {
      chartColors: beautifulChartColors,
      crosshairColor: '#C17A5A',
      tooltipBg: '#3D3530',
      tooltipText: '#E8E0D8',
      tooltipBorder: '#5c524a',
      gridColor: 'rgba(137, 122, 110, 0.15)',
      headerBg: '#5c524a',
      headerText: '#E8E0D8',
      buttonBg: '#3D3530',
      buttonText: '#E8E0D8',
      chartBg: '#ffffff',
      sidebarBg: '#E8E0D8',
      sidebarText: '#3D3530'
    };
  }

  const colors = theme.colors;

  // Chart lines ALWAYS use beautiful palette - never use theme chart colors
  // This ensures excellent waveform distinguishability regardless of theme

  // Special case: Espresso theme uses brighter, cream-colored backgrounds for better chart visibility
  const isEspresso = themeName === 'espresso';

  return {
    // Chart lines always use beautiful palette - NOT theme colors
    chartColors: beautifulChartColors,
    crosshairColor: colors['--primary'] || '#C17A5A',
    tooltipBg: isEspresso ? '#F5EDE5' : (colors['--card'] || '#3D3530'),
    tooltipText: isEspresso ? '#3D3530' : (colors['--card-foreground'] || '#E8E0D8'),
    tooltipBorder: isEspresso ? '#D4C4B5' : (colors['--border'] || '#5c524a'),
    gridColor: isEspresso ? 'rgba(80, 70, 60, 0.20)' : (colors['--border'] ? colors['--border'].replace(')', ' / 0.25)').replace('oklch(', 'oklch(') : 'rgba(137, 122, 110, 0.15)'),
    // Header bar uses muted colors for all themes
    headerBg: isEspresso ? '#5C524A' : (colors['--muted'] || '#5c524a'),
    headerText: isEspresso ? '#F5EDE5' : (colors['--muted-foreground'] || '#E8E0D8'),
    buttonBg: isEspresso ? '#3D3530' : (colors['--accent'] || '#5c524a'),
    buttonText: isEspresso ? '#F5EDE5' : (colors['--accent-foreground'] || '#E8E0D8'),
    chartBg: '#ffffff',
    // Left sidebar with checkboxes uses brighter colors
    sidebarBg: isEspresso ? '#E8DCD1' : (colors['--muted'] || '#E8E0D8'),
    sidebarText: isEspresso ? '#3D3530' : (colors['--foreground'] || '#3D3530'),
    // Dropdown specific colors
    dropdownBg: isEspresso ? '#FBF7F2' : (colors['--card'] || '#ffffff'),
    dropdownText: isEspresso ? '#5C4D3C' : (colors['--foreground'] || '#3D3530'),
    dropdownBorder: isEspresso ? '#DDD0C0' : (colors['--border'] || '#cccccc'),
    dropdownHover: isEspresso ? '#F0E6DA' : (colors['--secondary'] || '#f0f0f0'),
    dropdownSelected: isEspresso ? '#E5D9CC' : (colors['--accent'] || '#e0e0e0'),
    dropdownSelectedText: isEspresso ? '#3D3530' : (colors['--foreground'] || '#333333')
  };
};

// Add error handling for datasets with beautiful chart colors
// Chart waves always use these beautiful colors - NOT theme colors
const safeDatasets = (datasets, role = 'sweep') => {
  if (!datasets || !Array.isArray(datasets)) return [];

  // Select color palette based on chart role
  let colorPalette;
  let chartBg;
  if (role === 'volt') {
    colorPalette = voltageChartColors;
    chartBg = voltageChartBg;
  } else if (role === 'amp') {
    colorPalette = amperesChartColors;
    chartBg = amperesChartBg;
  } else {
    colorPalette = beautifulChartColors; // sweep/AC charts
    chartBg = '#ffffff';
  }

  return datasets.map((dataset, index) => {
    const chartColor = colorPalette[index % colorPalette.length];
    // Use dataset's existing color if it has one, otherwise use palette
    // This preserves colors set by NodesCheckList (via css_color)
    const hasExistingColor = dataset.borderColor && dataset.borderColor !== 'rgb(255, 99, 132)';
    const useColor = hasExistingColor ? dataset.borderColor : chartColor;
    return {
      ...dataset,
      backgroundColor: useColor,
      borderColor: useColor,
      borderWidth: 1.5,
      pointBackgroundColor: useColor,
      pointBorderColor: '#fff',
      pointBorderWidth: 0,
      pointRadius: 0, // Remove the little circles/points
      fill: false,
      tension: 0.4
    };
  });
};


const annotation = {
  annotations: [{
    type: 'line',
    mode: 'vertical',
    scaleID: 'x-axis',
    borderColor: '#b6fcd5',
    borderWidth: 2
  }]
};


const Chart = (props) => {
  // State to track the position of the draggable element

  const chartAmpRef = useRef(null);
  const chartVoltRef = useRef(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  // State to track if the initial burst of data has passed
  const [initialDataBurstComplete, setInitialDataBurstComplete] = useState(false);
  const dataArrivalInitiated = useRef(false);

  // Theme-aware colors state
  const [themeColors, setThemeColors] = useState(() => getChartThemeColors());

  // Listen for theme changes
  useEffect(() => {
    const handleThemeChange = () => {
      setThemeColors(getChartThemeColors());
    };

    // Listen for custom theme change event from ThemeManager
    window.addEventListener('themechange', handleThemeChange);

    // Initial load
    setThemeColors(getChartThemeColors());

    return () => {
      window.removeEventListener('themechange', handleThemeChange);
    };
  }, []);

  // Check if we have any actual data readings in the props
  const hasData = (props.chart_amperes_data?.datasets?.length > 0) ||
    (props.chart_volts_data?.datasets?.length > 0) ||
    (props.chart_VSweep_values?.datasets?.length > 0);

  useEffect(() => {
    if (hasData && !dataArrivalInitiated.current) {
      dataArrivalInitiated.current = true;
      // When the first data arrives, wait for a short duration (1 second) 
      // to let all initial batches/passes settle before showing the chart.
      // This ensures we ignore the entire first 'pass' which may consist of multiple updates.
      const timer = setTimeout(() => {
        setInitialDataBurstComplete(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [hasData]);

  // Determine if we should currently ignore the data
  const shouldIgnore = hasData && !initialDataBurstComplete;

  // Define data objects for display, potentially ignoring the initial settle period as requested
  const displayAmperesData = shouldIgnore ? { labels: [], datasets: [] } : props.chart_amperes_data;
  const displayVoltsData = shouldIgnore ? { labels: [], datasets: [] } : props.chart_volts_data;
  const displayVSweepValues = shouldIgnore ? { labels: [], datasets: [] } : props.chart_VSweep_values;

  const [isChartLoading, setChartLoading] = useState(false);
  const [dcSweepValue, setDcSweepValue] = useState(null);

  // Persistent state keys
  const DISPLAY_MODE_KEY = 'chart_displayMode';
  const PREVIOUS_MODE_KEY = 'chart_previousMode';
  const ORIGINAL_DIMENSIONS_KEY = 'chart_originalDimensions';

  // State for chart display mode: 'normal', 'small', 'minimized'
  const [displayMode, setDisplayMode] = useState(() => {
    return localStorage.getItem(DISPLAY_MODE_KEY) || 'normal';
  });
  // Store previous mode when minimizing
  const [previousMode, setPreviousMode] = useState(() => {
    return localStorage.getItem(PREVIOUS_MODE_KEY) || 'normal';
  });
  // Store original dimensions
  const [originalDimensions, setOriginalDimensions] = useState(() => {
    const stored = localStorage.getItem(ORIGINAL_DIMENSIONS_KEY);
    return stored ? JSON.parse(stored) : { width: '50%', left: '25%' };
  });

  // Persist state changes to localStorage
  useEffect(() => {
    localStorage.setItem(DISPLAY_MODE_KEY, displayMode);
  }, [displayMode]);

  useEffect(() => {
    localStorage.setItem(PREVIOUS_MODE_KEY, previousMode);
  }, [previousMode]);

  useEffect(() => {
    localStorage.setItem(ORIGINAL_DIMENSIONS_KEY, JSON.stringify(originalDimensions));
  }, [originalDimensions]);

  // State for responsiveness
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // State for controlled position
  const [controlledPosition, setControlledPosition] = useState(props.initialLocation);

  const [isDragging, setIsDragging] = useState(false);

  // Update position if initialLocation changes (e.g. from parent)
  useEffect(() => {
    setControlledPosition(props.initialLocation);
  }, [props.initialLocation]);

  // Update the position state on drag stop
  const handleDragStop = (e, data) => {
    setIsDragging(false);
    let { x, y } = data;
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;
    const minVisible = 50; // Minimum pixels of header to keep visible

    // Get the container width to check the left/right boundaries
    const node = data.node;
    const rect = node.getBoundingClientRect();
    const width = rect.width;

    // Smart repositioning (tween back) logic

    // 1. Vertical constraint: Header must be visible (top >= 0)
    if (y < 0) y = 0;

    // 2. Right boundary: Don't let the whole thing go off right
    if (x > windowWidth - minVisible) {
      x = windowWidth - minVisible;
    }

    // 3. Left boundary: Don't let it go off left completely
    if (x + width < minVisible) {
      x = minVisible - width;
    }

    // 4. Bottom boundary: Keep at least a bit of the header on screen
    if (y > windowHeight - 40) {
      y = windowHeight - 40;
    }

    setControlledPosition({ x, y });
    window.thumbs.lab("setSelectionOff");
    window.thumbs.lab("setPlotLocation", { x, y });
    window.thumbs.lab("setCircuitOnOff", "on");
  };

  const handleDragStart = (e, data) => {
    setIsDragging(true);
    window.thumbs.lab("setCircuitOnOff", "off");
  };

  const handleDrag = (e, data) => {
    setControlledPosition({ x: data.x, y: data.y });
  };

  const checkLabels = (data) => {
    // Check if the datasets key exists and is an array
    if (!data.datasets || !Array.isArray(data.datasets)) {
      return "";
    }

    // Check if all labels start with "I("
    const allStartWithI = data.datasets.every(dataset =>
      typeof dataset.label === 'string' && dataset.label.startsWith("I(")
    );

    if (allStartWithI) return "A";

    // Check if all labels start with "V"
    const allStartWithV = data.datasets.every(dataset =>
      typeof dataset.label === 'string' && dataset.label.startsWith("V")
    );

    if (allStartWithV) return "V";

    // If neither condition is met, return an empty string
    return "";
  }

  let sweep_options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      crosshair: {
        line: {
          color: themeColors.crosshairColor,
          width: 1
        },
        sync: {
          enabled: true,
          group: 3,
          suppressTooltips: false
        }
      },
      tooltip: {
        enabled: true,
        mode: 'index',
        intersect: false,
        position: 'nearest',
        callbacks: {
          label: function (tooltipItem, data) {
            tooltipItem.label = tooltipItem.label;
            tooltipItem.formattedValue = tooltipItem.formattedValue;
            return data;
          }
        },
        backgroundColor: themeColors.tooltipBg,
        titleColor: themeColors.tooltipText,
        bodyColor: themeColors.tooltipText,
        borderColor: themeColors.tooltipBorder,
        borderWidth: 1,
        padding: 10,
        cornerRadius: 4,
        displayColors: true,
        usePointStyle: true,
        boxPadding: 4
      },
      legend: {
        position: 'top',
      },
      title: {
        display: false,
        text: '',
      },
    },
    scales: {
      x: {
        _ticks: {
          // Include a dollar sign in the ticks
          callback: function (value, index, ticks) {
            return window.thumbs.correctAndRoundNumber(value) + "V";
          }
        },
        grid: {
          drawOnChartArea: true,
          color: themeColors.gridColor,
        }
      },
      y: {
        ticks: {
          // Include a dollar sign in the ticks
          callback: function (value, index, ticks) {
            return window.thumbs.correctAndRoundNumber(value) + checkLabels(displayVSweepValues);//VERTICAL AXIS LABELS
          }
        },
        grid: {
          drawBorder: true,
          color: themeColors.gridColor,
        }
      }
    }
  }

  let ac_sweep_options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      crosshair: {
        line: {
          color: themeColors.crosshairColor,
          width: 1
        },
        sync: {
          enabled: true,
          group: 3,
          suppressTooltips: false
        }
      },
      tooltip: {
        enabled: true,
        mode: 'index',
        intersect: false,
        position: 'nearest',
        callbacks: {
          label: function (tooltipItem, data) {
            tooltipItem.label = tooltipItem.label;
            tooltipItem.formattedValue = tooltipItem.formattedValue;
            return data;
          }
        },
        backgroundColor: themeColors.tooltipBg,
        titleColor: themeColors.tooltipText,
        bodyColor: themeColors.tooltipText,
        borderColor: themeColors.tooltipBorder,
        borderWidth: 1,
        padding: 10,
        cornerRadius: 4,
        displayColors: true,
        usePointStyle: true,
        boxPadding: 4
      },
      legend: {
        position: 'top',
      },
      title: {
        display: false,
        text: '',
      },
    },
    scales: {
      x: {
        grid: {
          drawOnChartArea: true,
          color: themeColors.gridColor,
        }
      },
      y: {
        position: 'left',
        ticks: {
          callback: function (value, index, ticks) {
            return window.thumbs.correctAndRoundNumber(value);
          }
        },
        grid: {
          drawBorder: true,
          color: themeColors.gridColor,
        }
      },
      y1: {
        position: 'right',
        ticks: {
          callback: function (value, index, ticks) {
            return window.thumbs.correctAndRoundNumber(value) + "°";
          }
        },
        grid: {
          drawOnChartArea: false,
          drawBorder: true,
          color: themeColors.gridColor,
        }
      }
    }
  };


  let volts_options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      crosshair: {
        line: {
          color: themeColors.crosshairColor,
          width: 1
        },
        sync: {
          enabled: true,
          group: 1,
          suppressTooltips: false
        }
      },
      tooltip: {
        enabled: true,
        mode: 'nearest',
        axis: 'x',
        intersect: false,
        position: 'nearest',
        callbacks: {
          title: function (tooltipItems) {
            if (tooltipItems.length > 0) {
              return window.thumbs.correctAndRoundNumber(tooltipItems[0].parsed.x) + "S";
            }
            return '';
          },
          label: function (tooltipItem) {
            return tooltipItem.dataset.label + ': ' + window.thumbs.correctAndRoundNumber(tooltipItem.parsed.y) + "V";
          }
        },
        backgroundColor: themeColors.tooltipBg,
        titleColor: themeColors.tooltipText,
        bodyColor: themeColors.tooltipText,
        borderColor: themeColors.tooltipBorder,
        borderWidth: 1,
        padding: 10,
        cornerRadius: 4,
        displayColors: true,
        usePointStyle: true,
        boxPadding: 4
      },
      legend: {
        position: 'top',
      },
      title: {
        display: false,
        text: '',
      },
    },
    scales: {
      x: {
        type: 'linear',
        ticks: {
          callback: function (value) {
            return window.thumbs.correctAndRoundNumber(value) + "S";
          },
          maxTicksLimit: 10,
        },
        grid: {
          drawOnChartArea: true,
          color: themeColors.gridColor,
        }
      },
      y: {
        // Headroom above the data maximum (and below the minimum only when
        // the data goes negative) so a flat level such as a 555's LED
        // current does not sit on the chart border. Unlike Chart.js `grace`
        // this never pushes a 0 A / 0 V floor below zero.
        afterDataLimits: (scale) => {
          const range = scale.max - scale.min;
          const pad = (range > 0 ? range : Math.abs(scale.max) || 1) * 0.05;
          scale.max += pad;
          if (scale.min < 0) scale.min -= pad;
        },
        ticks: {
          callback: function (value, index, ticks) {
            return window.thumbs.correctAndRoundNumber(value) + "V";
          }
        },
        grid: {
          drawBorder: true,
          color: themeColors.gridColor,
        }
      }
    }
  };




  let ampere_options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      crosshair: {
        line: {
          color: themeColors.crosshairColor,
          width: 1
        },
        sync: {
          enabled: true,
          group: 1,
          suppressTooltips: false
        }
      },
      tooltip: {
        enabled: true,
        mode: 'nearest',
        axis: 'x',
        intersect: false,
        position: 'nearest',
        callbacks: {
          title: function (tooltipItems) {
            if (tooltipItems.length > 0) {
              return window.thumbs.correctAndRoundNumber(tooltipItems[0].parsed.x) + "S";
            }
            return '';
          },
          label: function (tooltipItem) {
            return tooltipItem.dataset.label + ': ' + window.thumbs.correctAndRoundNumber(tooltipItem.parsed.y) + "A";
          }
        },
        backgroundColor: themeColors.tooltipBg,
        titleColor: themeColors.tooltipText,
        bodyColor: themeColors.tooltipText,
        borderColor: themeColors.tooltipBorder,
        borderWidth: 1,
        padding: 10,
        cornerRadius: 4,
        displayColors: true,
        usePointStyle: true,
        boxPadding: 4
      },
      legend: {
        position: 'top',
      },
      title: {
        display: false,
        text: '',
      },
    },
    scales: {
      x: {
        type: 'linear',
        ticks: {
          callback: function (value) {
            return window.thumbs.correctAndRoundNumber(value) + "S";
          },
          maxTicksLimit: 10,
        },
        grid: {
          drawOnChartArea: true,
          color: themeColors.gridColor,
        }
      },
      y: {
        // Headroom above the data maximum (and below the minimum only when
        // the data goes negative) so a flat level such as a 555's LED
        // current does not sit on the chart border. Unlike Chart.js `grace`
        // this never pushes a 0 A / 0 V floor below zero.
        afterDataLimits: (scale) => {
          const range = scale.max - scale.min;
          const pad = (range > 0 ? range : Math.abs(scale.max) || 1) * 0.05;
          scale.max += pad;
          if (scale.min < 0) scale.min -= pad;
        },
        ticks: {
          callback: function (value, index, ticks) {
            return window.thumbs.correctAndRoundNumber(value) + "A";
          }
        },
        grid: {
          drawBorder: true,
          color: themeColors.gridColor,
        }
      }
    }
  };

  const options = {

    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false,
    },
    elements: {
      point: {
        radius: 0 // Hide the points by setting radius to 0
      },
      line: {
        tension: 0.4 // Adjust the tension for smoothness
      }
    },
  };

  volts_options = { ...volts_options, ...options }
  ampere_options = { ...ampere_options, ...options }


  const moveCrosshairToIndex = (index) => {

    if (props.chart_amperes_data.datasets.length === 0) return;
    if (!chartAmpRef.current) return;

    const chart = chartAmpRef.current;
    const xScale = chart.scales['x'];
    const yScale = chart.scales['y'];
    if (xScale == null || yScale == null) return;
    const dataPoint = props.chart_amperes_data.datasets[0].data[index];
    if (dataPoint == null) return;
    const xPixel = xScale.getPixelForValue(dataPoint.x != null ? dataPoint.x : index);
    const yPixel = yScale.getPixelForValue(dataPoint.y != null ? dataPoint.y : dataPoint);
    try {
      const event = new MouseEvent('mousemove', {
        clientX: xPixel + chart.canvas.getBoundingClientRect().left,
        clientY: yPixel + chart.canvas.getBoundingClientRect().top,
      });

      chart.canvas.dispatchEvent(event);
    } catch (exceptionVar) {

    }

  };

  window.moveCrosshairToIndex = moveCrosshairToIndex;

  useEffect(() => {
    setChartLoading(false);
  }, [props.selectedNodesValues]); // Dependency array with 'props.selectedNodesValues'

  const isSmall = isMobile || displayMode === 'small';

  return (
    <Draggable
      onStop={handleDragStop}
      onStart={handleDragStart}
      onDrag={handleDrag}
      position={controlledPosition}
      handle=".drag-handle"
      cancel=".no-drag"
    >
      <div
        className="draggable-container"
        style={{
          pointerEvents: 'auto', // Always auto to ensure interaction works
          position: 'absolute',
          zIndex: 100,
          width: isMobile ? '95%' : (displayMode === 'small' ? '35%' : displayMode === 'minimized' ? '200px' : '60%'), // Modified width for mobile
          left: isMobile ? '2.5%' : (displayMode === 'small' ? '35%' : displayMode === 'minimized' ? '40%' : '20%'), // Modified left for mobile
          padding: 0,
          margin: 0,
          marginTop: '0px',
          boxShadow: 'var(--shadow-xl)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius)',
          overflow: 'visible', // Changed from hidden to allow dropdowns to expand
          transition: isDragging ? 'none' : 'transform 0.4s cubic-bezier(0.2, 0.8, 0.2, 1), width 0.3s ease, left 0.3s ease',
        }}
      >
        <Grid style={{ margin: 0 }}>
          <Grid.Column width={16} style={{ backgroundColor: 'var(--card)', padding: 0, margin: 0 }}>
            <div className="drag-handle" style={{ cursor: 'move', backgroundColor: themeColors.headerBg, color: themeColors.headerText, display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: isSmall ? '8px 4px' : '8px 12px', height: '42px', borderBottom: '1px solid var(--border)', touchAction: 'none', borderRadius: 'var(--radius) var(--radius) 0 0' }}>
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', minWidth: 0, pointerEvents: 'auto' }}>
                {displayMode !== 'minimized' && (
                  <AnalysisPicker
                    analysisMode={props.analysisMode}
                    setAnalysisMode={props.setAnalysisMode}
                    dcSweepValue={props.pickedDCSweepSource}
                    setDcSweepValue={props.setPickedDcSweepSource}
                    dcSweepOptions={props.DCSweepSources}
                    setNodes={props.setNodes}
                    isMobile={isMobile}
                    displayMode={displayMode}
                    themeColors={themeColors}
                  />
                )}
                {displayMode === 'minimized' && (
                  <div style={{ fontWeight: 'bold' }}>Chart (Minimized)</div>
                )}
              </div>
              <div style={{ display: 'flex', padding: isSmall ? '0 0 0 4px' : '0 0 0 12px', flexShrink: 0, pointerEvents: 'auto' }}>
                {/* Buttons in order: resize, minimize, close */}
                {displayMode !== 'minimized' && (
                  <Button
                    icon
                    size='mini'
                    className="no-drag"
                    style={{ marginRight: '8px', padding: '0', height: '28px', width: '28px', display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: themeColors.buttonBg, color: themeColors.buttonText }}
                    onClick={() => {
                      if (displayMode === 'normal') {
                        setDisplayMode('small');
                      } else {
                        setDisplayMode('normal');
                      }
                    }}
                  >
                    <Icon name={displayMode === 'small' ? 'expand' : 'compress'} style={{ margin: '0' }} />
                  </Button>
                )}

                {/* Minimize/Maximize button */}
                <Button
                  icon
                  size='mini'
                  className="no-drag"
                  style={{ display: 'none', marginRight: '8px', padding: '0', height: '28px', width: '28px', justifyContent: 'center', alignItems: 'center', backgroundColor: themeColors.buttonBg, color: themeColors.buttonText }}
                  onClick={() => {
                    if (displayMode === 'minimized') {
                      setDisplayMode(previousMode);
                    } else {
                      setPreviousMode(displayMode);
                      setDisplayMode('minimized');
                    }
                  }}
                >
                  <Icon name={displayMode === 'minimized' ? 'window maximize' : 'window minimize'} style={{ margin: '0' }} />
                </Button>

                {/* Close button - keeping only this one */}
                <Button
                  icon
                  size='mini'
                  className="no-drag"
                  style={{ padding: '0', height: '28px', width: '28px', display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: themeColors.buttonBg, color: themeColors.buttonText }}
                  onClick={() => {
                    // Close the chart properly
                    props.setNodes([]);
                    com.clientside.main.Html5Bootstrap.jsCall("setNGSpiceOff");
                  }}
                >
                  <Icon name='close' style={{ margin: '0' }} />
                </Button>
              </div>
            </div>

          </Grid.Column>
        </Grid>

        {displayMode !== 'minimized' && props.analysisMode === 'time_domain' && (
          <Grid style={{ margin: 0 }} stackable={isMobile}>
            {/* Left Column with Title */}

            <Grid.Column width={2} style={{ backgroundColor: themeColors.sidebarBg, color: themeColors.sidebarText, padding: '10px 0px 10px 5px', margin: 0 }}>
              <CheckboxList
                top={5}
                label='Amps'
                role={'component'}
                initialSelectedLabels={props.initialSelectedLabels}
                setChartLoading={props.setChartLoading}
                isChartLoading={props.isChartLoading}
                selectedNodesValues={props.selectedNodesValues}
                setSelectedNodes={props.setSelectedNodes}
                setSelectedNodesValues={props.setSelectedNodesValues}
                nodes={props.currentable_names}
                isMobile={isSmall}
              />
            </Grid.Column>

            {/* Center Column */}
            <Grid.Column width={14} style={{ padding: '0 0 0 0', margin: 0 }}>
              <div
                className="chart-container"
                style={{
                  backgroundColor: amperesChartBg,
                  padding: '5px',
                  borderRadius: 'var(--radius)',
                  border: '1px solid var(--border)'
                }}
              >
                <Line
                  ref={chartAmpRef}
                  key="chart_1"
                  style={{ maxHeight: 200, height: 200 }}
                  options={ampere_options}
                  data={{
                    ...displayAmperesData,
                    datasets: safeDatasets(displayAmperesData?.datasets, 'amp')
                  }}
                />
              </div>
            </Grid.Column>

          </Grid>
        )}
        {props.analysisMode === 'time_domain' && displayMode !== 'minimized' && (
          <Grid style={{ margin: 0 }} stackable={isMobile}>
            {/* Left Column with Title */}
            <Grid.Column width={2} style={{ backgroundColor: themeColors.sidebarBg, color: themeColors.sidebarText, padding: '10px 0px 10px 5px', margin: 0 }}>

              <CheckboxList
                top={0}
                style={{ position: 'relative' }}
                role={'volt'}
                label='Volts'
                initialSelectedLabels={props.initialSelectedLabels}
                setChartLoading={props.setChartLoading}
                isChartLoading={props.isChartLoading}
                selectedNodesValues={props.selectedNodesValues}
                setSelectedNodes={props.setSelectedNodes}
                setSelectedNodesValues={props.setSelectedNodesValues}
                nodes={props.nodes}
                isMobile={isSmall}
              />
            </Grid.Column>

            {/* Center Column */}
            <Grid.Column width={14} style={{ padding: '0px 0px 0px 0', margin: 0 }}>
              <div
                className="chart-container"
                style={{
                  backgroundColor: voltageChartBg,
                  padding: '5px',
                  borderRadius: 'var(--radius)',
                  border: '1px solid var(--border)'
                }}
              >
                <Line
                  ref={chartVoltRef}
                  key="chart_2"
                  style={{ maxHeight: 200, height: 200 }}
                  options={volts_options}
                  data={{
                    ...displayVoltsData,
                    datasets: safeDatasets(displayVoltsData?.datasets, 'volt')
                  }}
                />

              </div>
            </Grid.Column>



          </Grid>
        )}

        {props.analysisMode === 'dc_sweep' && displayMode !== 'minimized' && (
          <div style={{ margin: 0 }}>
            <Grid style={{ margin: 0 }} stackable={isMobile}>
              {/* Left Column with Title */}
              <Grid.Column width={2} style={{ marginTop: 0, backgroundColor: themeColors.sidebarBg, color: themeColors.sidebarText, padding: '10px 0px 10px 5px' }}>

                <CheckboxList
                  top={5}
                  style={{ position: 'relative' }}
                  role={'metric'}
                  label='Metric'
                  initialSelectedLabels={props.initialSelectedLabels}
                  setChartLoading={props.setChartLoading}
                  isChartLoading={props.isChartLoading}
                  selectedNodesValues={props.selectedNodesValues}
                  setSelectedNodes={props.setSelectedNodes}
                  setSelectedNodesValues={props.setSelectedNodesValues}
                  nodes={props.metricNodes}
                  isMobile={isSmall}
                />
              </Grid.Column>

              {/* Center Column */}
              <Grid.Column width={14} style={{ padding: '0px 0px 0px 0', margin: 0 }}>
                <div
                  className="chart-container"
                  style={{
                    marginTop: 0, backgroundColor: '#ffffff',
                    padding: '5px',
                    borderRadius: 'var(--radius)',
                    border: '1px solid var(--border)'
                  }}
                >
                  <Line
                    key="chart_3"
                    style={{ maxHeight: 400, height: 400 }}
                    options={sweep_options}
                    data={{
                      ...displayVSweepValues,
                      datasets: safeDatasets(displayVSweepValues?.datasets, 'sweep')
                    }}
                  />

                </div>
              </Grid.Column>



            </Grid>
          </div>
        )}

        {props.analysisMode === 'ac_sweep' && displayMode !== 'minimized' && (
          <div style={{ margin: 0 }}>
            <Grid style={{ margin: 0 }} stackable={isMobile}>
              {/* Left Column with Title - AC Sweep shows voltage nodes for frequency response */}
              <Grid.Column width={2} style={{ marginTop: 0, backgroundColor: themeColors.sidebarBg, color: themeColors.sidebarText, padding: '10px 0px 10px 5px' }}>

                <CheckboxList
                  top={5}
                  style={{ position: 'relative' }}
                  role={'volt'}
                  label='V(f)'
                  initialSelectedLabels={props.initialSelectedLabels}
                  setChartLoading={props.setChartLoading}
                  isChartLoading={props.isChartLoading}
                  selectedNodesValues={props.selectedNodesValues}
                  setSelectedNodes={props.setSelectedNodes}
                  setSelectedNodesValues={props.setSelectedNodesValues}
                  nodes={props.nodes}
                  isMobile={isSmall}
                />
              </Grid.Column>

              {/* Center Column */}
              <Grid.Column width={14} style={{ padding: '0px 0px 0px 0', margin: 0 }}>
                <div
                  className="chart-container"
                  style={{
                    marginTop: 0, backgroundColor: '#ffffff',
                    padding: '5px',
                    borderRadius: 'var(--radius)',
                    border: '1px solid var(--border)'
                  }}
                >
                  <Line
                    key="chart_4"
                    style={{ maxHeight: 400, height: 400 }}
                    options={ac_sweep_options}
                    data={{
                      ...displayVSweepValues,
                      datasets: safeDatasets(displayVSweepValues?.datasets, 'sweep')
                    }}
                  />

                </div>
              </Grid.Column>



            </Grid>
          </div>
        )}

        {/* Show a message when minimized */}
        {displayMode === 'minimized' && (
          <div style={{
            padding: '10px',
            textAlign: 'center',
            backgroundColor: 'var(--muted)',
            borderTop: '1px solid var(--border)',
            fontSize: '12px',
            color: 'var(--muted-foreground)'
          }}>
            Click the maximize button to restore
          </div>
        )}

      </div>
    </Draggable>
  );
};

export default Chart;




